"use client";

import { createContext, useContext, useState, useRef, useEffect, ReactNode, useCallback } from 'react';

// Animation state types
type AnimationState = 'idle' | 'waiting' | 'running' | 'paused';

// Configuration interface
interface LivingCardsConfig {
  enabled: boolean;     // Whether animations are enabled
  userEnabled: boolean; // User preference setting
  interval: number;     // Initial delay before animations start
  duration: number;     // How long each card's animation lasts
  staggerDelay: number; // Time between starting new animations (creates overlap)
}

// Context interface
interface LivingCardsContextType {
  animationState: AnimationState;
  registerHover: (id: string | null) => void;
  hoveredCardId: string | null;
  config: LivingCardsConfig;
  registerCard: (element: HTMLElement) => () => void;
}

// Create context with default value
const LivingCardsContext = createContext<LivingCardsContextType | null>(null);

// Hook to use the context
export function useLivingCards() {
  const context = useContext(LivingCardsContext);
  if (!context) {
    throw new Error('useLivingCards must be used within a LivingCardsProvider');
  }
  return context;
}

// Default configuration values
const defaultConfig: LivingCardsConfig = {
  enabled: true,
  userEnabled: true,
  interval: 10000,       // Initial delay before animations start
  duration: 1000,        // How long each card's animation lasts
  staggerDelay: 200      // Time between starting new animations (creates overlap)
};

// Read CSS variables to get configuration - only called client-side
function readCssVariables(): LivingCardsConfig {
  if (typeof document === 'undefined') {
    return defaultConfig;
  }
  
  const root = document.documentElement;
  const getVar = (name: string, defaultValue: string) => 
    getComputedStyle(root).getPropertyValue(name).trim() || defaultValue;
  
  // Parse the enabled setting
  const enabledValue = getVar('--living-cards-enabled', defaultConfig.enabled ? '1' : '0');
  const enabled = enabledValue === '1';
  
  // Parse the interval (wait time)
  const intervalValue = getVar('--living-cards-interval', defaultConfig.interval.toString());
  const interval = parseInt(intervalValue);
  
  // Parse the duration (animation time)
  const durationValue = getVar('--living-cards-duration', defaultConfig.duration.toString());
  const duration = parseInt(durationValue);
  
  // Parse stagger delay (animation timing parameter)
  const staggerDelayValue = getVar('--living-cards-stagger-delay', defaultConfig.staggerDelay.toString());
  const staggerDelay = parseInt(staggerDelayValue);
  
  return {
    enabled,
    userEnabled: enabled, // Initialize with the CSS setting
    interval,
    duration,
    staggerDelay
  };
}

// Provider component
export function LivingCardsProvider({ children }: { children: ReactNode }) {
  // Animation state management
  const [animationState, setAnimationState] = useState<AnimationState>('idle');
  const [hoveredCardId, setHoveredCardId] = useState<string | null>(null);
  
  // Configuration from CSS variables with client-side initialization
  const [config, setConfig] = useState<LivingCardsConfig>(defaultConfig);
  const configRef = useRef<LivingCardsConfig>(defaultConfig);
  
  // Initialize config on client-side only
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const clientConfig = readCssVariables();
      setConfig(clientConfig);
      configRef.current = clientConfig;
    }
  }, []);
  
  // Collection of registered card elements for animation
  const animatedCardsRef = useRef(new Set<HTMLElement>());
  
  // Register card elements for animation
  const registerCard = useCallback((element: HTMLElement) => {
    animatedCardsRef.current.add(element);
    return () => {
      animatedCardsRef.current.delete(element);
    };
  }, []);
  
  // Effect to detect global hover state changes
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    if (hoveredCardId) {
      // Card is being hovered, pause animations
      setAnimationState('paused');
      
      // Clear any fake hover effects
      animatedCardsRef.current.forEach(card => {
        card.classList.remove('fake-hover');
        // Force a repaint/reflow
        void card.offsetWidth;
      });
    } else if (animationState === 'paused') {
      // Start waiting period when hover ends
      setAnimationState('waiting');
      const timer = setTimeout(() => {
        if (config.enabled) {
          setAnimationState('running');
        }
      }, config.interval);
      
      return () => clearTimeout(timer);
    }
  }, [hoveredCardId, animationState, config]);
  
  // Animation loop using requestAnimationFrame - sequential random animation
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    if (animationState !== 'running' || !config.enabled || hoveredCardId !== null) {
      return;
    }
    
    let rafId: number;
    let lastAnimationTime = 0;
    let lastCardIndex = -1; // Track which card was last animated
    
    const animate = (timestamp: number) => {
      // Use staggerDelay to create overlapping animations
      // Cards start animating at regular intervals, but their animations can overlap
      if (timestamp - lastAnimationTime > config.staggerDelay) {
        animateNextRandomCard();
        lastAnimationTime = timestamp;
      }
      
      rafId = requestAnimationFrame(animate);
    };
    
    const animateNextRandomCard = () => {
      // Only proceed if we have cards registered
      const cards = Array.from(animatedCardsRef.current);
      if (cards.length === 0) {
        return;
      }
      
      // Get a random card index, but make sure it's different from the last one
      let randomIndex;
      do {
        randomIndex = Math.floor(Math.random() * cards.length);
      } while (cards.length > 1 && randomIndex === lastCardIndex);
      
      lastCardIndex = randomIndex;
      const selectedCard = cards[randomIndex];
      
      // Don't clear existing animations - let them overlap
      // This is key to the staggered effect
      
      // Animate just the selected card
      selectedCard.classList.add('fake-hover');
      
      // Remove the animation class after duration
      setTimeout(() => {
        selectedCard.classList.remove('fake-hover');
      }, config.duration);
    };
    
    // Start animation loop
    rafId = requestAnimationFrame(animate);
    
    // Cleanup
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [animationState, hoveredCardId, config]);
  
  // Listen for prefers-reduced-motion changes
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    
    const handleChange = () => {
      // Update config based on user preference
      configRef.current = {
        ...configRef.current,
        enabled: !mediaQuery.matches && configRef.current.userEnabled
      };
      
      // If animations should be disabled, pause them
      if (mediaQuery.matches && animationState === 'running') {
        setAnimationState('paused');
      }
    };
    
    // Initial check
    handleChange();
    
    // Listen for changes
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [animationState]);
  
  // Initialize animation state when component mounts
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    // Force a direct transition to running state after initial timeout
    // This is a separate effect specifically to handle initialization
    const initialTimer = setTimeout(() => {
      if (config.enabled && !hoveredCardId) {
        setAnimationState('running');
      }
    }, config.interval);
    
    return () => clearTimeout(initialTimer);
  }, [config, hoveredCardId]);
  
  return (
    <LivingCardsContext.Provider value={{
      animationState,
      registerHover: setHoveredCardId,
      hoveredCardId,
      config, // Use the state value instead of the ref for better reactivity
      registerCard
    }}>
      {children}
    </LivingCardsContext.Provider>
  );
}